import { useEffect, useMemo, useState } from 'react';
import { endOfDay, parse as parseDate, startOfDay } from 'date-fns';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { fetchModels, fetchStatsByPlayer } from '../api';
import type { ApiEndpointModel, ApiPlayerModelRow, ApiPlayerStatsResponse } from '../types';

type RangeKey = '24h' | '7d' | '30d' | 'custom';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 days',
  '30d': '30 days',
  custom: 'Custom',
};

/** Window length for the rolling presets. Absent for `custom`, which is explicit. */
const RANGE_MS: Record<'24h' | '7d' | '30d', number> = {
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
};

/** The `yyyy-MM-dd` pair behind the custom range, as `<input type="date">` gives it. */
interface CustomRange {
  from: string;
  to: string;
}

const EMPTY_CUSTOM_RANGE: CustomRange = { from: '', to: '' };

interface ResolvedRange {
  from: Date;
  to: Date;
}

function parseDayInput(value: string): Date | null {
  if (!value) return null;
  const parsed = parseDate(value, 'yyyy-MM-dd', new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Turn the selected preset into an absolute window. Returns null only for a
 * custom selection that isn't a usable range yet — an endpoint still missing, or
 * `from` after `to` — which the page prompts for rather than querying.
 *
 * A custom range covers whole days in local time, so picking one day yields that
 * day in full rather than a zero-length window.
 */
function resolveRange(
  key: RangeKey,
  custom: CustomRange,
  now: Date = new Date(),
): ResolvedRange | null {
  if (key === 'custom') {
    const from = parseDayInput(custom.from);
    const to = parseDayInput(custom.to);
    if (!from || !to) return null;
    const window = { from: startOfDay(from), to: endOfDay(to) };
    return window.from > window.to ? null : window;
  }
  return { from: new Date(now.getTime() - RANGE_MS[key]), to: now };
}

/** Human label for a resolved window, shown next to the preset buttons. */
function describeRange(range: ResolvedRange): string {
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${fmt.format(range.from)} – ${fmt.format(range.to)}`;
}

// Slice colours for the endpoint splits, matching the Statistics page's cost pie.
// Assigned per endpoint rather than per slice, so a model keeps one colour
// across the window-wide donut and every player's own.
const MODEL_COLORS = [
  '#6366f1',
  '#0ea5e9',
  '#8b5cf6',
  '#14b8a6',
  '#f59e0b',
  '#ec4899',
  '#64748b',
];

/** Calls that carried no player are grouped under an empty player by the server. */
const UNATTRIBUTED_LABEL = 'Unattributed';

function formatUsd(value: number): string {
  if (value <= 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(5)}`;
  return `$${value.toFixed(4)}`;
}

/**
 * Per-player usage for the API-wrapper (apigen) section: how many calls and how
 * much spend an average job of theirs takes, and which endpoints they lean on.
 * Aggregation is server-side (`GET /v1/stats/by-player`), which also leaves QA
 * load out, so the numbers describe how people really use the service.
 */
export function PlayersPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('24h');
  const [custom, setCustom] = useState<CustomRange>(EMPTY_CUSTOM_RANGE);
  const [data, setData] = useState<ApiPlayerStatsResponse | null>(null);
  const [models, setModels] = useState<ApiEndpointModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Keyed by player rather than row index, so an expanded split survives a refetch.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Model labels rarely change; load them once.
  useEffect(() => {
    let cancelled = false;
    fetchModels()
      .then((m) => {
        if (!cancelled) setModels(m);
      })
      .catch(() => {
        /* labels are best-effort; fall back to raw model_type */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resolved = useMemo(
    () => resolveRange(rangeKey, custom),
    // reloadKey re-snapshots "now" so Refresh re-fetches the latest data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeKey, custom, reloadKey],
  );

  useEffect(() => {
    if (!resolved) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchStatsByPlayer({ from: resolved.from, to: resolved.to })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolved]);

  const labelFor = useMemo(() => {
    const map = new Map(models.map((m) => [m.key, m.label]));
    return (key: string) => map.get(key) || key;
  }, [models]);

  function toggle(player: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(player)) next.delete(player);
      else next.add(player);
      return next;
    });
  }

  const totals = data?.totals;
  const players = data?.by_player || [];
  const windowSplit = data?.by_model || [];

  // The window-wide split already ranks every endpoint anyone used, so ranking
  // colours by it once gives each model the same colour in every chart here.
  const colorFor = useMemo(() => {
    const map = new Map(
      (data?.by_model || []).map((row, i) => [
        row.model_type,
        MODEL_COLORS[i % MODEL_COLORS.length],
      ]),
    );
    return (key: string) => map.get(key) || MODEL_COLORS[MODEL_COLORS.length - 1];
  }, [data]);

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Players</h1>
        <p className="text-sm text-slate-500 mt-1">
          Usage per player in the selected time frame. QA load is excluded.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setRangeKey(key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                rangeKey === key
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {RANGE_LABELS[key]}
            </button>
          ))}
        </div>

        {/* The date pair only appears under Custom, so the common case stays one row. */}
        {rangeKey === 'custom' ? (
          <div className="inline-flex items-center gap-1.5">
            <input
              type="date"
              value={custom.from}
              max={custom.to || undefined}
              onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              aria-label="From date"
              className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={custom.to}
              min={custom.from || undefined}
              onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              aria-label="To date"
              className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        ) : (
          resolved && (
            <span className="text-[11px] text-slate-400 tabular-nums">
              {describeRange(resolved)}
            </span>
          )
        )}

        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          title="Refresh"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
          </svg>
          Refresh
        </button>
      </div>
    </div>
  );

  // A half-filled custom range covers no window. Whatever is still in state was
  // measured over the previously selected range, so showing it here would put
  // numbers next to a period they don't describe. Prompt instead.
  if (!resolved) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        {header}
        <div className="rounded-lg border border-slate-200 bg-slate-50 text-slate-600 px-4 py-3 text-sm">
          Pick a start and end date to load player usage for a custom period.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {header}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load player statistics: {error}
        </div>
      )}

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Players" value={String(totals?.players ?? 0)} sub="with calls in this period" />
        <StatCard label="Jobs" value={String(totals?.jobs ?? 0)} sub={`${totals?.calls ?? 0} calls total`} />
        <StatCard
          label="Avg calls / job"
          value={(totals?.avg_calls_per_job ?? 0).toFixed(1)}
          sub="across every player"
        />
        <StatCard
          label="Avg cost / job"
          value={formatUsd(totals?.avg_cost_per_job ?? 0)}
          sub="across every player"
        />
        <StatCard label="Total cost" value={formatUsd(totals?.cost_usd ?? 0)} sub="in this period" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Per-player table; a row expands into that player's endpoint split */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">By player</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">{players.length}</span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">rows</span>
            </div>
          </div>
          {loading && !data ? (
            <div className="flex items-center justify-center text-sm text-slate-500 py-8">
              Loading player statistics…
            </div>
          ) : players.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {/* Column header */}
              <div className="hidden sm:grid grid-cols-[1fr_repeat(5,minmax(0,5.5rem))] gap-2 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <span>Player</span>
                <span className="text-right">Calls</span>
                <span className="text-right">Jobs</span>
                <span className="text-right">Calls / job</span>
                <span className="text-right">Cost / job</span>
                <span className="text-right">Cost</span>
              </div>
              {players.map((row) => {
                const isOpen = expanded.has(row.player);
                return (
                  <div key={row.player || UNATTRIBUTED_LABEL}>
                    <button
                      type="button"
                      onClick={() => toggle(row.player)}
                      aria-expanded={isOpen}
                      className="w-full text-left grid grid-cols-2 sm:grid-cols-[1fr_repeat(5,minmax(0,5.5rem))] gap-2 py-2.5 text-sm items-center hover:bg-slate-50"
                    >
                      <span className="flex items-center gap-1.5 min-w-0 col-span-2 sm:col-span-1">
                        <svg
                          className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span
                          className={`truncate ${
                            row.player ? 'font-medium text-slate-800' : 'italic text-slate-500'
                          }`}
                          title={row.player || UNATTRIBUTED_LABEL}
                        >
                          {row.player || UNATTRIBUTED_LABEL}
                        </span>
                      </span>
                      <span className="text-right tabular-nums text-slate-700" data-label="Calls">
                        <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Calls</span>
                        {row.calls}
                      </span>
                      <span className="text-right tabular-nums text-slate-700" data-label="Jobs">
                        <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Jobs</span>
                        {row.jobs}
                      </span>
                      <span className="text-right tabular-nums text-slate-700" data-label="Calls / job">
                        <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Calls / job</span>
                        {row.avg_calls_per_job.toFixed(1)}
                      </span>
                      <span className="text-right tabular-nums text-slate-700" data-label="Cost / job">
                        <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Cost / job</span>
                        {formatUsd(row.avg_cost_per_job)}
                      </span>
                      <span className="text-right tabular-nums text-slate-700" data-label="Cost">
                        <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Cost</span>
                        {formatUsd(row.cost_usd)}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pb-4 pl-0 sm:pl-5">
                        <ModelSplit
                          rows={row.by_model}
                          labelFor={labelFor}
                          colorFor={colorFor}
                          title={
                            row.player
                              ? `Endpoints used by ${row.player}`
                              : 'Endpoints used by calls with no player'
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center text-sm text-slate-400 py-8">
              No calls in this period
            </div>
          )}
        </div>

        {/* The whole window's endpoint split, for context on the rows beside it */}
        <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Model usage</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">{totals?.calls ?? 0}</span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">calls</span>
            </div>
          </div>
          {loading && !data ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500 py-8">
              Loading player statistics…
            </div>
          ) : windowSplit.length > 0 ? (
            <ModelSplit rows={windowSplit} labelFor={labelFor} colorFor={colorFor} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 py-8">
              No calls in this period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Share of calls per endpoint as a donut plus a legend. `share` comes from the
 * server, so the percentages are of calls (not cost) and always sum to 100%.
 */
function ModelSplit({
  rows,
  labelFor,
  colorFor,
  title,
}: {
  rows: ApiPlayerModelRow[];
  labelFor: (key: string) => string;
  colorFor: (key: string) => string;
  title?: string;
}) {
  const slices = rows.map((row) => ({
    key: row.model_type,
    name: labelFor(row.model_type),
    value: row.calls,
    share: row.share,
    cost: row.cost_usd,
    color: colorFor(row.model_type),
  }));
  if (slices.length === 0) {
    return <p className="text-xs text-slate-400 py-2">No calls recorded.</p>;
  }

  return (
    <div>
      {title && <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">{title}</p>}
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="52%"
              outerRadius="80%"
              paddingAngle={2}
              stroke="none"
            >
              {slices.map((slice) => (
                <Cell key={slice.key} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                fontSize: '12px',
              }}
              formatter={(value: number, name: string) => [`${value} calls`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-3 space-y-1.5">
        {slices.map((slice) => (
          <li key={slice.key} className="flex items-center gap-2 text-xs">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: slice.color }}
            />
            <span className="text-slate-600 truncate flex-1" title={slice.name}>
              {slice.name}
            </span>
            <span className="tabular-nums font-medium text-slate-700 w-10 text-right">
              {(slice.share * 100).toFixed(0)}%
            </span>
            <span className="tabular-nums text-slate-500 w-16 text-right">
              {formatUsd(slice.cost)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
